<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("agent_commission.settings_title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card>
                <el-tabs v-model="activeTab">
                    <!-- ============ PROFILES TAB ============ -->
                    <el-tab-pane :label="$t('agent_commission.profiles_title')" name="profiles">
                        <div class="row">
                            <!-- Left: profile list -->
                            <div class="col-md-4">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h4 class="mb-0">{{ $t("agent_commission.profiles_title") }}</h4>
                                    <base-button type="success" size="sm" @click="openNewProfile">
                                        <i class="fa fa-plus mr-1"></i>{{ $t("agent_commission.new_profile") }}
                                    </base-button>
                                </div>
                                <div v-if="profiles.length === 0" class="text-muted py-3">
                                    {{ $t("agent_commission.empty_profiles") }}
                                </div>
                                <ul class="list-group">
                                    <li
                                        v-for="p in profiles"
                                        :key="p.id"
                                        class="list-group-item d-flex justify-content-between align-items-center"
                                        :class="{ active: selectedProfileId === p.id }"
                                        style="cursor: pointer;"
                                        @click="selectProfile(p.id)"
                                    >
                                        <span>
                                            {{ p.name }}
                                            <span v-if="p.is_default" class="badge badge-primary ml-1">
                                                {{ $t("agent_commission.default_badge") }}
                                            </span>
                                        </span>
                                        <small class="text-muted">
                                            {{ $t("agent_commission.agent_count") }}: {{ p.agent_count || 0 }}
                                        </small>
                                    </li>
                                </ul>
                            </div>

                            <!-- Right: tier editor for the selected profile -->
                            <div class="col-md-8">
                                <div v-if="!editing" class="text-muted text-center py-5">
                                    {{ $t("agent_commission.select_profile_hint") }}
                                </div>
                                <div v-else>
                                    <div class="row align-items-end mb-3">
                                        <div class="col-md-6">
                                            <base-input :label="$t('agent_commission.profile_name')">
                                                <el-input v-model="editing.name"></el-input>
                                            </base-input>
                                        </div>
                                        <div class="col-md-6 pb-3">
                                            <el-checkbox v-model="editing.is_default" :disabled="editing.was_default">
                                                {{ $t("agent_commission.set_default") }}
                                            </el-checkbox>
                                        </div>
                                    </div>

                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <h5 class="mb-0">{{ $t("agent_commission.tier.percent") }}</h5>
                                        <base-button type="success" size="sm" @click="addTier">
                                            <i class="fa fa-plus mr-1"></i>{{ $t("agent_commission.tier.add_tier") }}
                                        </base-button>
                                    </div>

                                    <div v-if="editing.tiers.length === 0" class="text-center text-muted py-3">
                                        {{ $t("agent_commission.empty_tiers") }}
                                    </div>

                                    <el-table v-else :data="editing.tiers" header-row-class-name="thead-light" class="w-100">
                                        <el-table-column :label="$t('agent_commission.tier.min_amount')" min-width="150">
                                            <div slot-scope="{ $index }">
                                                <el-input-number
                                                    v-model="editing.tiers[$index].min_amount"
                                                    :min="0"
                                                    :precision="2"
                                                    controls-position="right"
                                                    size="small"
                                                    class="w-100"
                                                ></el-input-number>
                                            </div>
                                        </el-table-column>
                                        <el-table-column :label="$t('agent_commission.tier.max_amount')" min-width="170">
                                            <div slot-scope="{ $index }">
                                                <el-input-number
                                                    v-model="editing.tiers[$index].max_amount"
                                                    :min="0"
                                                    :precision="2"
                                                    controls-position="right"
                                                    size="small"
                                                    class="w-100"
                                                    :placeholder="$t('agent_commission.tier.max_amount_hint')"
                                                ></el-input-number>
                                                <small class="text-muted">{{ $t("agent_commission.tier.max_amount_hint") }}</small>
                                            </div>
                                        </el-table-column>
                                        <el-table-column :label="$t('agent_commission.tier.percent')" min-width="130">
                                            <div slot-scope="{ $index }">
                                                <el-input-number
                                                    v-model="editing.tiers[$index].percent"
                                                    :min="0"
                                                    :max="100"
                                                    :precision="2"
                                                    controls-position="right"
                                                    size="small"
                                                    class="w-100"
                                                ></el-input-number>
                                            </div>
                                        </el-table-column>
                                        <el-table-column :label="$t('actions')" align="right" min-width="70">
                                            <div slot-scope="{ $index }">
                                                <base-button type="danger" size="sm" icon @click="removeTier($index)">
                                                    <i class="fa fa-trash"></i>
                                                </base-button>
                                            </div>
                                        </el-table-column>
                                    </el-table>

                                    <div v-if="validationError" class="text-danger mt-3">
                                        {{ validationError }}
                                    </div>

                                    <div class="mt-3">
                                        <base-button type="primary" :loading="saving" @click="saveProfile">
                                            {{ $t("agent_commission.tier.save_tiers") }}
                                        </base-button>
                                        <el-tooltip
                                            v-if="editing.id"
                                            :content="deleteTooltip"
                                            :disabled="!deleteDisabled"
                                            placement="top"
                                        >
                                            <span class="ml-2 d-inline-block">
                                                <base-button type="danger" :disabled="deleteDisabled" @click="confirmDeleteProfile">
                                                    {{ $t("delete") }}
                                                </base-button>
                                            </span>
                                        </el-tooltip>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- ============ AGENTS TAB ============ -->
                    <el-tab-pane :label="$t('agent_commission.agents_tab')" name="agents">
                        <div v-if="agents.length === 0" class="text-center text-muted py-4">
                            {{ $t("agent_commission.empty_agents_list") }}
                        </div>
                        <el-table v-else :data="agents" header-row-class-name="thead-light" v-loading="agentsLoading">
                            <el-table-column :label="$t('agent_commission.agent.agent_name')" prop="name"></el-table-column>
                            <el-table-column :label="$t('agent_commission.agent_profile')" min-width="220">
                                <div slot-scope="{ row }">
                                    <el-select
                                        v-model="row.agent_commission_profile_id"
                                        size="small"
                                        class="w-100"
                                        clearable
                                        :placeholder="$t('agent_commission.use_default')"
                                        @change="onAgentProfileChange(row, $event)"
                                    >
                                        <el-option :label="$t('agent_commission.use_default')" :value="null"></el-option>
                                        <el-option
                                            v-for="p in profiles"
                                            :key="p.id"
                                            :label="p.name"
                                            :value="p.id"
                                        ></el-option>
                                    </el-select>
                                </div>
                            </el-table-column>
                        </el-table>
                    </el-tab-pane>
                </el-tabs>
            </card>
        </div>
    </div>
</template>

<script>
import { Table, TableColumn, InputNumber, Input, Tabs, TabPane, Checkbox, Select, Option, Tooltip } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";

export default {
    name: "AgentCommissionSettings",
    components: {
        RouteBreadCrumb,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [InputNumber.name]: InputNumber,
        [Input.name]: Input,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Checkbox.name]: Checkbox,
        [Select.name]: Select,
        [Option.name]: Option,
        [Tooltip.name]: Tooltip,
    },
    data() {
        return {
            activeTab: "profiles",
            selectedProfileId: null,
            editing: null, // { id, name, is_default, was_default, tiers:[], agent_count }
            saving: false,
            validationError: "",
            agentsLoading: false,
        };
    },
    computed: {
        ...mapGetters({
            profiles: "AgentCommission/getProfiles",
            agents: "AgentCommission/getAgents",
        }),
        deleteDisabled() {
            if (!this.editing) return true;
            return this.editing.was_default || (this.editing.agent_count || 0) > 0;
        },
        deleteTooltip() {
            if (!this.editing) return "";
            if (this.editing.was_default) return this.$t("agent_commission.cannot_delete_default");
            if ((this.editing.agent_count || 0) > 0) return this.$t("agent_commission.cannot_delete_in_use");
            return "";
        },
    },
    mounted() {
        this.loadProfiles();
        this.loadAgents();
    },
    methods: {
        ...mapActions({
            fetchProfiles: "AgentCommission/fetchProfiles",
            fetchProfile: "AgentCommission/fetchProfile",
            createProfile: "AgentCommission/createProfile",
            updateProfile: "AgentCommission/updateProfile",
            deleteProfileAction: "AgentCommission/deleteProfile",
            fetchAgents: "AgentCommission/fetchAgents",
            setAgentProfile: "AgentCommission/setAgentProfile",
        }),
        async loadProfiles() {
            try {
                await this.fetchProfiles();
                if (this.profiles.length > 0 && this.selectedProfileId == null) {
                    this.selectProfile(this.profiles[0].id);
                }
            } catch (err) {
                // handled in module
            }
        },
        async loadAgents() {
            this.agentsLoading = true;
            try {
                await this.fetchAgents();
            } catch (err) {
                // handled in module
            } finally {
                this.agentsLoading = false;
            }
        },
        async selectProfile(id) {
            this.selectedProfileId = id;
            this.validationError = "";
            try {
                const resp = await this.fetchProfile(id);
                const item = (resp.data.Data && resp.data.Data[0]) || {};
                const profile = item.profile || {};
                const tiers = item.tiers || [];
                this.editing = {
                    id: profile.id,
                    name: profile.name || "",
                    is_default: !!profile.is_default,
                    was_default: !!profile.is_default,
                    agent_count: profile.agent_count || 0,
                    tiers: tiers.map(t => ({
                        min_amount: Number(t.min_amount) || 0,
                        max_amount: t.max_amount != null ? Number(t.max_amount) : null,
                        percent: Number(t.percent) || 0,
                    })),
                };
            } catch (err) {
                this.editing = null;
            }
        },
        openNewProfile() {
            this.selectedProfileId = null;
            this.validationError = "";
            this.editing = {
                id: null,
                name: "",
                is_default: false,
                was_default: false,
                agent_count: 0,
                tiers: [],
            };
        },
        addTier() {
            this.editing.tiers.push({ min_amount: 0, max_amount: null, percent: 0 });
        },
        removeTier(index) {
            this.editing.tiers.splice(index, 1);
        },
        validate() {
            const tiers = this.editing.tiers;
            if (!this.editing.name || this.editing.name.trim() === "") {
                return this.$t("agent_commission.validation.name_required");
            }
            for (let i = 0; i < tiers.length; i++) {
                const t = tiers[i];
                if (t.min_amount == null || t.min_amount === "") {
                    return this.$t("agent_commission.validation.min_required");
                }
                if (t.percent < 0 || t.percent > 100) {
                    return this.$t("agent_commission.validation.percent_range");
                }
                if (i > 0 && t.min_amount < tiers[i - 1].min_amount) {
                    return this.$t("agent_commission.validation.not_ascending");
                }
            }
            return "";
        },
        async saveProfile() {
            this.validationError = this.validate();
            if (this.validationError) return;

            this.saving = true;
            try {
                const payload = {
                    name: this.editing.name,
                    is_default: this.editing.is_default,
                    tiers: this.editing.tiers,
                };
                if (this.editing.id) {
                    await this.updateProfile({ id: this.editing.id, payload });
                } else {
                    await this.createProfile(payload);
                }
                swal.fire({
                    title: this.$t("agent_commission.save_success"),
                    icon: "success",
                    confirmButtonClass: "btn btn-success btn-fill",
                    buttonsStyling: false,
                });
                const keepId = this.editing.id;
                await this.loadProfiles();
                if (keepId) {
                    await this.selectProfile(keepId);
                } else if (this.profiles.length > 0) {
                    await this.selectProfile(this.profiles[this.profiles.length - 1].id);
                }
                this.loadAgents();
            } catch (err) {
                this.showError(err);
            } finally {
                this.saving = false;
            }
        },
        confirmDeleteProfile() {
            swal.fire({
                title: this.$t("agent_commission.delete_profile_confirm"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonClass: "btn btn-danger btn-fill",
                cancelButtonClass: "btn btn-secondary btn-fill",
                confirmButtonText: this.$t("confirm"),
                cancelButtonText: this.$t("cancel"),
                buttonsStyling: false,
            }).then(async result => {
                if (result.value) {
                    try {
                        await this.deleteProfileAction(this.editing.id);
                        swal.fire({
                            title: this.$t("agent_commission.delete_success"),
                            icon: "success",
                            confirmButtonClass: "btn btn-success btn-fill",
                            buttonsStyling: false,
                        });
                        this.selectedProfileId = null;
                        this.editing = null;
                        await this.loadProfiles();
                    } catch (err) {
                        this.showError(err);
                    }
                }
            });
        },
        async onAgentProfileChange(row, value) {
            try {
                await this.setAgentProfile({ userId: row.user_id, profileId: value != null ? value : null });
                swal.fire({
                    title: this.$t("agent_commission.save_success"),
                    icon: "success",
                    timer: 1200,
                    showConfirmButton: false,
                    buttonsStyling: false,
                });
                this.loadProfiles();
            } catch (err) {
                this.showError(err);
                this.loadAgents();
            }
        },
        showError(err) {
            const msg = err.response && err.response.data && err.response.data.Cause
                ? err.response.data.Cause
                : err.message;
            swal.fire({ title: msg, icon: "error", confirmButtonClass: "btn btn-danger btn-fill", buttonsStyling: false });
        },
    },
};
</script>
